import { useState } from 'react';
import { AxiosResponse } from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal/DeleteConfirmationModal.component';
import Loading from '@/components/general/Loading';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import useCandidateStatuses from '@/hooks/useCandidateStatuses';
import { DEFAULT_SORT_BY } from '@/pages/CandidatesList/CandidatesList.constants';
import candidatesServices, { Candidate } from '@/services/candidates';
import jobsServices, { AddCandidatesToJobParams } from '@/services/jobs';
import { FetchApiIds } from '@/types';
import JobCandidatesComponent, { Layout } from './JobCandidates.component';

const JobCandidates = function (): JSX.Element {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [removeUser, setRemoveUser] = useState<string>();
  const [layout, setLayout] = useState<Layout>('list');
  const [candidateSearchInputText, setCandidateSearchInputText] = useState<string>('');
  const [isAddTalentOpen, setIsAddTalentOpen] = useState<boolean>(false);
  const candidateStatuses = useCandidateStatuses();
  const [sortBy, setSortBy] = useState<{ order: string; column: string }>(DEFAULT_SORT_BY);
  const toast = useToastContext();

  const {
    data: jobCandidates,
    refetch: refetchCandidatesByStage,
    isLoading: isLoadingCandidates,
  } = useApiFetch(
    [FetchApiIds.GET_JOB_CANDIDATES_BY_STAGE, jobId],
    async () => jobsServices.getJobCandidatesByStage({ jobId: jobId || '' }),
    { enabled: Boolean(jobId) }
  );

  const { data: candidateSearchResponse, isLoading: candidateSearchLoading } = useApiFetch<
    Candidate[]
  >(
    [FetchApiIds.CANDIDATE_SEARCH, candidateSearchInputText],
    async () => {
      const [nameResponse, skillResponse] = await Promise.all([
        candidatesServices.searchCandidate(candidateSearchInputText),
        candidatesServices.searchCandidatesBySkill(candidateSearchInputText),
      ]);

      const nameCandidates = nameResponse.data ?? [];
      const skillCandidates = skillResponse.data ?? [];

      const candidateMap = new Map<string, Candidate>();
      [...nameCandidates, ...skillCandidates].forEach((candidate) => {
        if (!candidateMap.has(candidate.id)) {
          candidateMap.set(candidate.id, candidate);
        }
      });

      return { data: Array.from(candidateMap.values()) } as AxiosResponse<Candidate[]>;
    },
    { enabled: candidateSearchInputText.length >= 3 }
  );

  const handleGoToProfile = (profileId: string): void => {
    navigate(`/hiring/candidates/${profileId}`);
  };

  const handleRemoveCandidateClick = (profileId: string): void => {
    setRemoveUser(profileId);
  };

  const handleChangeLayout = (value: Layout): void => {
    setLayout(value);
  };

  const addCandidatesToJobMutation = useApiSubmit<unknown, AddCandidatesToJobParams>(
    jobsServices.addCandidatesToJob
  );

  const handleAddTalent = async (talentId: string): Promise<void> => {
    try {
      await addCandidatesToJobMutation.mutateAsync({
        jobId: jobId || '',
        candidateIds: [talentId],
      });
      toast.showSuccessFeedback('Candidate added successfully');
      refetchCandidatesByStage();
    } catch {
      toast.showErrorFeedback('There was a problem to add the candidate to this job');
    }
  };

  const removeCandidatesToJobMutation = useApiSubmit<unknown, AddCandidatesToJobParams>(
    jobsServices.removeCandidatesToJob
  );

  const handleRemoveConfirm = async (): Promise<void> => {
    if (removeUser) {
      try {
        await removeCandidatesToJobMutation.mutateAsync({
          jobId: jobId || '',
          candidateIds: [removeUser],
        });
        toast.showSuccessFeedback('Candidate removed successfully');
        refetchCandidatesByStage();
      } catch {
        toast.showErrorFeedback('There was a problem removing the candidate from this job');
      } finally {
        setRemoveUser(undefined);
      }
    }
  };

  if (isLoadingCandidates) {
    return <Loading fullPage />;
  }

  return (
    <>
      <JobCandidatesComponent
        candidates={jobCandidates}
        onGoToProfile={handleGoToProfile}
        candidateSearchInputText={candidateSearchInputText}
        setCandidateSearchInputText={setCandidateSearchInputText}
        onRemoveCandidate={handleRemoveCandidateClick}
        layout={layout}
        onChangeLayout={handleChangeLayout}
        onAddTalent={handleAddTalent}
        isAddTalentOpen={isAddTalentOpen}
        setIsAddTalentOpen={setIsAddTalentOpen}
        candidateStatuses={candidateStatuses}
        sortBy={sortBy}
        setSortBy={setSortBy}
        allCandidates={candidateSearchResponse}
        candidateSearchLoading={candidateSearchLoading}
      />
      <DeleteConfirmationModal
        isOpen={Boolean(removeUser)}
        onClose={() => setRemoveUser(undefined)}
        onConfirmHandler={handleRemoveConfirm}
        isButtonDisabled={removeCandidatesToJobMutation.isLoading}
        onConfirmLabel="Yes, remove it"
      />
    </>
  );
};

export default JobCandidates;
